import fetch from 'isomorphic-fetch';
import { has } from 'lodash';
import axios from 'axios';
import { API_ERROR } from '../../constants/actions';
import { localToRemoteUrl } from '../../helpers/urls';
import {
  getAccessToken,
  getOktaAccessToken,
  shouldRenewAccessToken,
} from '../../helpers/localStorage';
import { deauthUserAndUnsubscribeTopics } from '../auth-utils';
import { getCreationSource } from '../../helpers/creation-source';
import { IN_TESTING_MODE, TEST_AUTH_TOKEN } from '../../tests/constants';
import { serializeError } from '@app/utils/serialize-error';
import i18n from '@app/i18n';

const { CancelToken } = axios;
const UNAUTH = 'UNAUTH';
const DEBUG = false;

export function validateAuthErrorAndDispatchDeauth(error, dispatch) {
  if (error === UNAUTH) {
    dispatch(deauthUserAndUnsubscribeTopics());
  }
}

export async function makeRequest(opts) {
  const url = localToRemoteUrl(opts.endpoint, opts.host);

  let authTok = TEST_AUTH_TOKEN;
  if (!IN_TESTING_MODE) {
    // this ternary is used here to avoid a promise when possible
    // so that the api calls can be correctly parallelized,
    // please do not abstract the line below to a single promise helper function
    authTok = shouldRenewAccessToken()
      ? await getAccessToken()
      : getOktaAccessToken();
  }

  const includeCreationSource =
    opts.includeCreationSource !== undefined
      ? opts.includeCreationSource
      : true;

  const headers = {
    Authorization: authTok,
    'Accept-Language': i18n.language || 'en',
    ...(includeCreationSource
      ? { 'x-marley-creation-source': getCreationSource() }
      : {}),
  };

  const options = {
    url,
    method: opts.method || 'GET',
    headers,
  };

  if (has(opts, 'body')) {
    options.data = opts.body;
  }

  return axios(options);
}

export async function doRequest(opts) {
  if (DEBUG) console.log(opts);
  const url = localToRemoteUrl(opts.endpoint, opts.host);
  if (DEBUG) console.log('doRequest url', url);
  const includeCreationSource =
    opts.includeCreationSource !== undefined
      ? opts.includeCreationSource
      : true;

  let authTok = null;
  if (!opts.noAuth) {
    // Do NOT move this ternary into a helper function
    // because nested Promises with Redux dispatch can delay API calls.
    // Safe to refactor only after migrating away from Redux.
    authTok = shouldRenewAccessToken()
      ? await getAccessToken()
      : getOktaAccessToken();
  }

  const headers = opts.headers || {
    'content-type': 'application/json',
    Accept: 'application/json, application/xml, text/plain, text/html, *.*',
    Authorization: authTok,
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    Pragma: 'no-cache',
    Expires: 0,
    ...(includeCreationSource
      ? { 'x-marley-creation-source': getCreationSource() }
      : {}),
  };

  /**
   * This line is necessary to be backwards-compatible with old requests
   * that pass their own headers. This is an attempt to load the tokens
   * in the least amount of spots
   */
  headers.Authorization = authTok;
  headers['Accept-Language'] = i18n.language || 'en';

  const payload = {
    method: opts.method || 'GET',
    mode: opts.mode,
    headers: new Headers(headers),
    body: opts.body ? JSON.stringify(opts.body) : undefined,
    ...(opts.signal && { signal: opts.signal }),
  };
  const actionName = opts.action;
  const { dispatch } = opts;

  return fetch(url, payload)
    .then((response) => {
      let responseData;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        responseData = response.json();
      } else if (contentType && contentType.includes('image')) {
        responseData = response.blob();
      } else {
        responseData = response.text();
      }
      if (response.status >= 300) {
        if (response.status === 401 || response.status === 403) {
          return Promise.reject(UNAUTH);
        }

        return responseData.then((data) => Promise.reject(data));
      }
      return responseData;
    })
    .catch((error) => {
      // eslint-disable-next-line no-console
      console.log('fetch error in doRequest', error);
      const errorPayload = {
        error: serializeError(error),
      };
      if (dispatch) {
        if (opts.passBodyOnError) {
          dispatch({
            type: `REQUEST_${actionName}_ERROR`,
            payload: { ...errorPayload, body: opts?.body },
          });
        } else {
          dispatch({
            type: `REQUEST_${actionName}_ERROR`,
            payload: errorPayload,
          });
          if (opts.errorCallback)
            opts.errorCallback({ body: opts.body, error, dispatch });
        }
      }
      return Promise.reject(error);
    });
}

export const preApiCall = ({ dispatch, args }) => {
  const opts = { ...args };
  const actionName = args.action;
  if (args.preApiCallback) args.preApiCallback({ body: args.body, dispatch });
  if (actionName) {
    dispatch({ type: `REQUEST_${actionName}_INIT`, payload: args.initPayload });
  }
  return { opts: { ...opts }, actionName };
};

export const postApiCall = ({ body, dispatch, getStore, args, actionName }) => {
  if (body !== undefined && body !== null) {
    const processPayload = { body, dispatch, getStore };
    const process = args.process || ((t) => t);
    if (args.callback) {
      dispatch(args.callback(processPayload));
    }
    const actionDispatch = { type: actionName };
    const processedPayload = process(processPayload);
    if (processedPayload) {
      actionDispatch.payload = processedPayload;
    }
    if (actionName) {
      dispatch(actionDispatch);
      dispatch({
        type: `REQUEST_${actionName}_SUCCESS`,
        payload: args.successPayload,
      });
    }
  } else {
    dispatch({ type: API_ERROR, payload: { args } });
  }
};

export function cancelableRequest(args) {
  return async (dispatch, getStore) => {
    const { opts, actionName } = preApiCall({ dispatch, getStore, args });
    // Do NOT move this ternary into a helper function
    // because nested Promises with Redux dispatch can delay API calls.
    // Safe to refactor only after migrating away from Redux.
    const authTok = shouldRenewAccessToken()
      ? await getAccessToken()
      : getOktaAccessToken();

    // Build axios config
    // Support both 'body' (preferred, consistent with doRequest) and 'data' (legacy) property names
    const requestData = opts.body !== undefined ? opts.body : opts.data;
    const axiosConfig = {
      url: opts.endpoint,
      method: opts.method,
      headers: {
        Authorization: authTok,
        'Accept-Language': i18n.language || 'en',
      },
      cancelToken: opts.cancelToken && new CancelToken(opts.cancelToken),
      ...(opts.signal && { signal: opts.signal }),
      ...(requestData !== undefined && { data: requestData }),
    };

    return axios(axiosConfig)
      .then((response) => {
        if (response.status === 200) {
          return postApiCall({
            body: response.data,
            dispatch,
            getStore,
            args,
            actionName,
          });
        }

        return Promise.reject('search request is canceled');
      })
      .catch((error) => {
        console.log(error);
        dispatch(args.errorCallback());
      });
  };
}

export function apiCall({ args }) {
  if (!args)
    console.error("'args' field in parameter for apiCall() is undefined");
  return (dispatch, getStore) => {
    const { opts, actionName } = preApiCall({ dispatch, getStore, args });
    return doRequest({ ...opts, dispatch, actionName })
      .then((body) => {
        postApiCall({
          body,
          dispatch,
          getStore,
          args,
          actionName,
        });
        return Promise.resolve(body);
      })
      .catch((error) => {
        validateAuthErrorAndDispatchDeauth(error, dispatch);
        dispatch({ type: API_ERROR });
        dispatch({
          type: `REQUEST_${actionName}_ERROR`,
          payload: { error, body: args.body },
        });
        return Promise.reject(error);
      });
  };
}

export async function uploadFile(opts) {
  const url = localToRemoteUrl(opts.endpoint);
  const data = new FormData();
  data.append('file', opts.file);
  data.append('name', opts.name);
  // this ternary is used here to avoid a promise when possible
  // so that the api calls can be correctly parallelized,
  // please do not abstract the line below to a single promise helper function
  const authTok = shouldRenewAccessToken()
    ? await getAccessToken()
    : getOktaAccessToken();
  return axios({
    url,
    method: 'post',
    data,
    headers: {
      Authorization: authTok,
      'Accept-Language': i18n.language || 'en',
    },
    cancelToken: opts.cancelToken && new CancelToken(opts.cancelToken),
    ...(opts.signal && { signal: opts.signal }),
  });
}

export const getFile = async (url) => {
  const opts = {
    endpoint: url,
  };
  return doRequest(opts)
    .then((blob) => URL.createObjectURL(blob))
    .catch((err) => {
      console.error(err);
    });
};
